import React, { useState, useEffect } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import Cookies from "js-cookie";
import { jwtDecode } from "jwt-decode";
import { CircularProgress, Box } from "@mui/material";
import "@fontsource/inter";
// Layouts
import AdminLayout from "../admin/layout/adminlayout";
import UserInsightsLayout from "../pages/layout/UserInsightsLayout";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { LinkedInAuthProvider } from "../context/LinkedInAuthProvider";
// Pages
import Homepage from "../pages/homepage";
import SignupPage from "../pages/signupPage.js";
import LoginPage from "../pages/login";
import Forgotpassword from "../pages/forgotpassword";
import ForgotPasswordPage from "../pages/forgotpasswordpage.js";
import PricingPage from "../pages/pricingpage.js";
import PaymentSuccess from "../pages/paymentSuccessfull";
import PaymentCancelled from "../pages/paymentCancelled";
import Createsurvey from "../pages/CreateSurvey";
import DefineAudience from "../pages/defineAudience";
import GeneratingInsights from "../pages/GeneratingInsights";
import InsightsList from "../pages/InsightsList";
import ViewInsights from "../pages/viewinsight";
import AdvisorView from "../pages/advisor-view";
import LinkedInCallback from "../context/LinkedInCallback";
import PageNotFound from "../pages/pageNotFound";
import CreateOrg from "../pages/createorg.js";
import SolutionPage from "../pages/solutionPages/solutionpage.js";
import Howtouse from "../pages/howtouse.js";
import Aboutus from "../pages/aboutus.js";
import Privacy from "../pages/privacy.js";
import AddSurveySpecifications from "../pages/addSurveySpecificaltion.js";
import SurveyRobustness from "../pages/surveyRobustness.js";
import SurveyGenerated from "../pages/surveyGenerated.js";
// Admin pages
import Pricing from "../admin/pages/Subscription.jsx";
import Researchers from "../admin/pages/Members.jsx";
import Profile from "../admin/pages/Profile";
//superadmin
import SuperadminLayout from "../superadmin/layout.js";
import SuperadminDashboard from "../superadmin/dashboard.js";
import UserInvites from "../superadmin/freeuser.js";

// Route wrappers
import {
  ProtectedRoute,
  PublicRoute,
  MixedRoute,
  SignupFlowRoute,
} from "../components/ProtectedRoute";

let roleCache = null;
let roleCacheTimestamp = null;
const CACHE_DURATION = 5 * 60 * 1000;

export const getUserRole = async () => {
  if (
    roleCache &&
    roleCacheTimestamp &&
    Date.now() - roleCacheTimestamp < CACHE_DURATION
  ) {
    return roleCache;
  }

  try {
    const token = Cookies.get("token");
    if (!token) return null;

    const response = await fetch(
      `${process.env.REACT_APP_API_URL}/project/getuserPlanAndProject`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          token: token,
        },
        credentials: "include",
      },
    );

    const data = await response.json();
    if (data.status === "success" && data.role) {
      roleCache = data.role;
      roleCacheTimestamp = Date.now();
      return data.role;
    }

    return null;
  } catch (error) {
    console.error("Error fetching user role:", error);
    return null;
  }
};

export const clearRoleCache = () => {
  roleCache = null;
  roleCacheTimestamp = null;
};

export const getUserRoleSync = () => {
  return roleCache;
};

const SuperadminRoute = ({ children }) => {
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    const token = Cookies.get("token");
    if (!token) {
      setStatus("unauthed");
      return;
    }
    getUserRole().then((role) => {
      if (role === "superadmin") {
        setStatus("allowed");
      } else if (role) {
        setStatus("denied");
      } else {
        setStatus("unauthed");
      }
    });
  }, []);

  if (status === "loading") {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          backgroundColor: "#000",
        }}
      >
        <CircularProgress sx={{ color: "#3b82f6" }} />
      </Box>
    );
  }
  if (status === "unauthed") return <Navigate to="/log-in" replace />;
  if (status === "denied") return <Navigate to="/projects" replace />;
  return children;
};

const DynamicLayout = ({ children }) => {
  const [userRole, setUserRole] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchRole = async () => {
      const role = await getUserRole();
      setUserRole(role);
      setIsLoading(false);
    };

    fetchRole();
  }, []);

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          backgroundColor: "#000",
        }}
      >
        <CircularProgress sx={{ color: "#3b82f6" }} />
      </Box>
    );
  }

  const isAdmin = userRole === "admin";

  return isAdmin ? (
    <AdminLayout>{children}</AdminLayout>
  ) : (
    <UserInsightsLayout>{children}</UserInsightsLayout>
  );
};

const AppRoutes = () => {
  const [userRole, setUserRole] = useState(null);
  const [isLoadingRole, setIsLoadingRole] = useState(true);
  const GoogleAuthWrapper = () => {
    return (
      <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
        <LinkedInAuthProvider>
          <SignupPage />
        </LinkedInAuthProvider>
      </GoogleOAuthProvider>
    );
  };

  const GoogleAuthWrapper1 = () => {
    return (
      <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
        <LinkedInAuthProvider>
          <LoginPage />
        </LinkedInAuthProvider>
      </GoogleOAuthProvider>
    );
  };

  useEffect(() => {
    const fetchRole = async () => {
      const role = await getUserRole();
      setUserRole(role);
      setIsLoadingRole(false);
    };

    fetchRole();
  }, []);

  // Show loading screen while determining role
  if (isLoadingRole) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          backgroundColor: "#000",
        }}
      >
        <CircularProgress sx={{ color: "#3b82f6" }} />
      </Box>
    );
  }

  const isAdmin = userRole === "admin";

  return (
    <Routes>
      <Route
        path="/"
        element={
          <MixedRoute>
            <Homepage />
          </MixedRoute>
        }
      />

      {/* Public Routes */}
      <Route
        path="/sign-up"
        element={
          <PublicRoute>
            <GoogleAuthWrapper />
          </PublicRoute>
        }
      />

      <Route
        path="/log-in"
        element={
          <PublicRoute>
            <GoogleAuthWrapper1 />
          </PublicRoute>
        }
      />

      <Route
        path="/create-org"
        element={
          <SignupFlowRoute>
            <CreateOrg />
          </SignupFlowRoute>
        }
      />

      <Route
        path="/forgot-password"
        element={
          <PublicRoute>
            <Forgotpassword />
          </PublicRoute>
        }
      />
      <Route
        path="/forgot-password/:id/:token"
        element={
          <PublicRoute>
            <ForgotPasswordPage />
          </PublicRoute>
        }
      />

      <Route
        path="/pricing"
        element={
          <PublicRoute>
            <PricingPage />
          </PublicRoute>
        }
      />
      {isAdmin && (
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <AdminLayout />
            </ProtectedRoute>
          }
        >
          <Route path="profile" element={<Profile />} />
          <Route path="members" element={<Researchers />} />
          <Route path="subscription" element={<Pricing />} />
        </Route>
      )}

      <Route
        path="/projects"
        element={
          <ProtectedRoute>
            {userRole === "superadmin" ? (
              <Navigate to="/superadmin" replace />
            ) : isAdmin ? (
              <AdminLayout />
            ) : (
              <UserInsightsLayout />
            )}
          </ProtectedRoute>
        }
      >
        <Route index element={<InsightsList />} />
        <Route path="view/:projectId" element={<ViewInsights />} />
        <Route
          path="view/:projectId/advisor/:advisorId"
          element={<AdvisorView />}
        />
      </Route>
      <Route path="/linkedin/callback" element={<LinkedInCallback />} />
      <Route
        path="/create-survey"
        element={
          <PublicRoute>
            <Createsurvey />
          </PublicRoute>
        }
      />
      <Route
        path="/add-survey"
        element={
          <PublicRoute>
            <AddSurveySpecifications />
          </PublicRoute>
        }
      />
      <Route
        path="/survey-robustness"
        element={
          <PublicRoute>
            <SurveyRobustness />
          </PublicRoute>
        }
      />
      <Route
        path="/generated-survey"
        element={
          <PublicRoute>
            <SurveyGenerated />
          </PublicRoute>
        }
      />
      <Route
        path="/define-audience"
        element={
          <PublicRoute>
            <DefineAudience />
          </PublicRoute>
        }
      />
      <Route
        path="/generate-insights"
        element={
          <ProtectedRoute>
            <GeneratingInsights />
          </ProtectedRoute>
        }
      />

      {/* Payment Routes */}
      <Route
        path="/payment-success"
        element={
          <ProtectedRoute>
            <PaymentSuccess />
          </ProtectedRoute>
        }
      />

      <Route
        path="/payment-cancelled"
        element={
          <MixedRoute>
            <PaymentCancelled />
          </MixedRoute>
        }
      />

      {/* Solution Pages */}
      <Route
        path="/paymentcancel"
        element={
          <MixedRoute>
            <PaymentCancelled />
          </MixedRoute>
        }
      />
      <Route
        path="/solution"
        element={
          <PublicRoute>
            <SolutionPage />
          </PublicRoute>
        }
      />
      <Route
        path="/how-it-works"
        element={
          <PublicRoute>
            <Howtouse />
          </PublicRoute>
        }
      />

      {/* About & Privacy */}
      <Route
        path="/about"
        element={
          <PublicRoute>
            <Aboutus />
          </PublicRoute>
        }
      />
      <Route
        path="/privacy-policy"
        element={
          <PublicRoute>
            <Privacy />
          </PublicRoute>
        }
      />
      {/* Superadmin */}
      <Route
        path="/superadmin"
        element={
          <SuperadminRoute>
            <SuperadminLayout />
          </SuperadminRoute>
        }
      >
        <Route index element={<SuperadminDashboard />} />
        <Route path="orgs" element={<UserInvites />} />
      </Route>

      {/* 404 */}
      <Route path="*" element={<PageNotFound />} />
    </Routes>
  );
};

export default AppRoutes;

export { DynamicLayout };
